import { t, useLanguage } from "@/lib/i18n";
import React, { useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";

let library;
function loadGoogle() {
  if (window.google?.accounts?.id) return Promise.resolve();
  if (!library) library = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.onload = resolve;
    script.onerror = () => { script.remove(); library = null; reject(new Error("Could not load Google sign-in. Please reload and try again.")); };
    document.head.appendChild(script);
  });
  return library;
}

export default function GoogleSignIn({ link = false, onSuccess }) {
  const language = useLanguage();
  const host = useRef(null);
  const callback = useRef(onSuccess);
  callback.current = onSuccess;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    let cancelled = false;
    const setup = async () => {
      try {
        const { data } = await api.get("/auth/google/config");
        if (!data.client_id || cancelled) return;
        await loadGoogle();
        if (cancelled) return;
        window.google.accounts.id.initialize({ client_id: data.client_id, auto_select: false,
          callback: async ({ credential }) => {
            if (cancelled) return;
            setBusy(true); setError("");
            try {
              const result = await api.post(link ? "/auth/google/link" : "/auth/google", { credential });
              if (!cancelled) await callback.current(result.data);
            } catch (err) {
              if (!cancelled) setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
            } finally { if (!cancelled) setBusy(false); }
          } });
        window.google.accounts.id.renderButton(host.current, { theme: "outline", size: "large", locale: language, text: link ? "continue_with" : "signin_with" });
      } catch (err) { if (!cancelled) setError("Google sign-in is unavailable. Please try again later."); }
    };
    setup();
    return () => { cancelled = true; };
  }, [link, language]);
  return <div className="space-y-2">
    <div ref={host} className={`flex justify-center ${busy ? "pointer-events-none opacity-60" : ""}`} />
    {busy && <p role="status" className="text-sm text-stone-600">{t("Connecting to Google...")}</p>}
    {error && <p role="alert" className="text-sm text-red-700">{t(error)}</p>}
  </div>;
}
